import React, { useState, useEffect, useRef, useCallback, memo } from 'react';
import { Link } from 'react-router-dom';
import { useCategories } from '../context/CategoriesContext';
import SEOHead from '../components/SEOHead';
import Loader from '../components/Loader';
import LuxuryCashbackDisplay from '../components/LuxuryCashbackDisplay';
import { useLazyLoading, useImagePreloader } from '../hooks/useLazyLoading';
import './Home.css';
// Responsive video URLs for different device sizes
const videoCDNUrls = {
  mobile: "https://images.vastrebyvijh.com/vastre-images/hero-video/hero-video-480.webm",
  tablet: "https://images.vastrebyvijh.com/vastre-images/hero-video/hero-video-720.webm", 
  desktop: "https://images.vastrebyvijh.com/vastre-images/hero-video/hero-video-1080.webm"
};

// Function to determine appropriate video quality based on device size and connection speed
const getVideoQuality = (windowWidth, connectionSpeed) => {
  // For slow connections, always use mobile quality
  if (connectionSpeed === 'slow') {
    return 'mobile';
  }
  
  // For medium connections, use tablet quality for larger screens
  if (connectionSpeed === 'medium') {
    return windowWidth <= 768 ? 'mobile' : 'tablet';
  }
  
  // For fast connections, use appropriate quality based on screen size
  if (windowWidth <= 480) {
    return 'mobile';
  } else if (windowWidth <= 1024) {
    return 'tablet';
  } else {
    return 'desktop';
  }
};

// Utility function to generate picture element sources
// Using <picture> elements lets the browser automatically select the optimal image size
// based on device capabilities, viewport size, and network conditions
const generatePictureSources = (imageSizes, sizes = "(max-width: 600px) 100vw, (max-width: 1200px) 50vw, 33vw") => {
  if (!imageSizes || typeof imageSizes !== 'object') return { sources: [], sizes };
  
  // Order sources from smallest to largest for optimal browser selection
  // Browser will automatically pick the best size based on:
  // - Device pixel density (1x, 2x, 3x)
  // - Viewport width
  // - Network conditions
  // - User preferences
  const sizeOrder = ['480w', '768w', '1080w', '1440w', '1920w'];
  const sources = [];
  
  sizeOrder.forEach(size => {
    if (imageSizes[size]) {
      sources.push({
        srcset: imageSizes[size],
        media: size === '480w' ? '(max-width: 480px)' :
               size === '768w' ? '(max-width: 768px)' :
               size === '1080w' ? '(max-width: 1080px)' :
               size === '1440w' ? '(max-width: 1440px)' :
               '(min-width: 1441px)'
      });
    }
  });
  
  return { sources, sizes };
};

// Memoized About Section Component for better performance
const AboutSection = memo(({ aboutImages, aboutImagesLoading }) => {
  const { isVisible, ref } = useLazyLoading({ threshold: 0.1, rootMargin: '100px' });

  return (
    <section className="about-section" ref={ref}>
      <div className="about-content">
        <div className="about-text">
          <h2>About Us</h2>
          <p>Vastré By Vijh was founded with a singular vision — to revive the essence of bespoke couture. Each creation is handcrafted with precision, using the finest fabrics, and designed for the discerning individual who values sophistication and detail.</p>
          <Link to="/about" className="home-discover-cta">
            DISCOVER OUR STORY
          </Link>
        </div>
        <div className="about-image">
          {aboutImagesLoading ? (
            <div className="about-image-loading">
              <div className="loading-spinner"></div>
              <p>Loading About Images...</p>
            </div>
          ) : aboutImages.length > 0 && aboutImages[0] && aboutImages[0].sizes ? (
            <picture>
              {(() => {
                const { sources, sizes } = generatePictureSources(aboutImages[0].sizes, "(max-width: 600px) 100vw, (max-width: 1200px) 50vw, 40vw");
                return sources.map((source, index) => (
                  <source
                    key={index}
                    srcSet={source.srcset}
                    media={source.media}
                    type="image/webp"
                    sizes={sizes}
                  />
                ));
              })()}
              <img
                src={aboutImages[0].sizes?.fallback || aboutImages[0].sizes?.original}
                alt={aboutImages[0].alt}
                className="about-suit-image"
                loading={isVisible ? "eager" : "lazy"}
                sizes="(max-width: 600px) 100vw, (max-width: 1200px) 50vw, 40vw"
                fetchpriority={isVisible ? "high" : "auto"}
                onError={(e) => {
                  console.error('About image failed to load:', e.target.src);
                }}
              />
            </picture>
          ) : (
            <div className="no-image-placeholder">
              <p>No about image available</p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
});

// Debounce utility function
const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

const Home = () => {
  const [isTurned, setIsTurned] = useState(false);
  const [heroImages, setHeroImages] = useState([]);
  const [isVideoLoading, setIsVideoLoading] = useState(true);
  const [showVideoContent, setShowVideoContent] = useState(true); // Show content immediately
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  
  // Simple video loading with direct URL
  const videoRef = useRef(null);
  const [videoLoadingProgress, setVideoLoadingProgress] = useState(0);
  const [connectionSpeed, setConnectionSpeed] = useState('fast');
  
  // Detect connection speed for adaptive loading
  useEffect(() => {
    if ('connection' in navigator) {
      const connection = navigator.connection;
      const effectiveType = connection.effectiveType;
      
      if (effectiveType === 'slow-2g' || effectiveType === '2g' || effectiveType === '3g') {
        setConnectionSpeed('slow');
      } else if (effectiveType === '4g') {
        setConnectionSpeed('medium');
      } else {
        setConnectionSpeed('fast');
      }
    } else {
      // Fallback: assume slow if no connection API
      setConnectionSpeed('slow');
    }
  }, []);

  // Optimized video loading with intersection observer
  useEffect(() => {
    if (!videoRef.current) return;

    const video = videoRef.current;
    let isVideoStarted = false;

    // Simplified loading strategy - let HTML media queries handle source selection
    const startVideoLoading = () => {
      if (isVideoStarted) return;
      isVideoStarted = true;

      // Start loading immediately
      video.preload = 'metadata'; // Load metadata for better UX
      setVideoLoadingProgress(10);
    };

    // Intersection Observer for smart loading
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !isVideoStarted) {
            startVideoLoading();
          }
        });
      },
      { threshold: 0.1, rootMargin: '50px' }
    );

    observer.observe(video);

    // Event handlers for progressive loading
    const handleLoadedMetadata = () => {
      setVideoLoadingProgress(30);
      // Switch to auto preload for progressive loading
      video.preload = 'auto';
    };

    const handleProgress = () => {
      if (video.buffered.length > 0) {
        const bufferedEnd = video.buffered.end(video.buffered.length - 1);
        const duration = video.duration;
        if (duration > 0) {
          const progress = 30 + ((bufferedEnd / duration) * 60); // 30% to 90%
          setVideoLoadingProgress(Math.min(progress, 90));
        }
      }
    };

    const handleCanPlay = () => {
      setVideoLoadingProgress(100);
      setIsVideoLoading(false);
      setShowVideoContent(true);
      // Start playing immediately
      video.play().catch(err => console.log('Auto-play prevented:', err));
    };

    const handleError = (e) => {
      console.error('Video loading error:', e);
      setIsVideoLoading(false);
      setShowVideoContent(true);
    };

    // Add event listeners
    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    video.addEventListener('progress', handleProgress);
    video.addEventListener('canplay', handleCanPlay);
    video.addEventListener('error', handleError);

    // Cleanup
    return () => {
      observer.disconnect();
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      video.removeEventListener('progress', handleProgress);
      video.removeEventListener('canplay', handleCanPlay);
      video.removeEventListener('error', handleError);
    };
  }, []);

  // Fallback timer to hide loader if video takes too long
  useEffect(() => {
    const fallbackTimer = setTimeout(() => {
      setIsVideoLoading(false);
      setShowVideoContent(true); // Show video content even if video fails
    }, 3000); // Hide loader after 3 seconds max for faster UX

    return () => clearTimeout(fallbackTimer);
  }, []);
  const [heroImagesLoading, setHeroImagesLoading] = useState(true);
  const [aboutImages, setAboutImages] = useState([]);
  const [aboutImagesLoading, setAboutImagesLoading] = useState(true);
  const [logoImage, setLogoImage] = useState('');
  const [logoLoading, setLogoLoading] = useState(true);
  const { categories, loading: categoriesLoading } = useCategories();
  
  // Filter to show only the 4 specific collections on home page
  const homePageCollections = categories.filter(category => 
    ['shirts', 'blazer', 'ethnic-sets', 'pants'].includes(category.id)
  );
  
  // Video autoplay is now handled by the FirebaseVideoStreamer component
  
  // SEO and structured data for homepage
  const homePageSEO = {
    title: "Premium Men's Fashion | Custom Tailored Clothing Online",
    description: "Shop premium men's fashion online at Vastré By Vijh (VastreByVijh). Custom tailored suits, designer kurtas, ethnic wear, formal shirts, and premium menswear. Perfect fit guaranteed with personalized measurements. Buy online at vastrebyvijh.com",
    keywords: "vastrebyvijh, VastreByVijh, vastre by vijh, premium men's fashion, online men's clothing, custom tailored suits, designer menswear, ethnic wear online, kurta for men, formal wear online, wedding suits, designer kurta, sherwani, bandhgala, indo western, men's fashion India, premium menswear, tailored clothing online, vastrebyvijh.com",
    image: "https://images.vastrebyvijh.com/vastre-images/home-hero/desktop/2025-08-26/home-hero_desktop_1756239228357_0_1920w.webp",
    url: "https://vastrebyvijh.com/",
    type: "website",
    structuredData: {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "name": "VastreByVijh",
      "alternateName": ["Vastré By Vijh", "Vastre By Vijh", "vastrebyvijh"],
      "url": "https://vastrebyvijh.com",
      "description": "VastreByVijh - Premium Custom Made Men's Clothing & Bespoke Fashion with Perfect Fit by Vastré By Vijh",
      "potentialAction": {
        "@type": "SearchAction",
        "target": "https://vastrebyvijh.com/search?q={search_term_string}",
        "query-input": "required name=search_term_string"
      }
    }
  };
  
  // Debug: Log categories data structure
  useEffect(() => {
    // Categories data loaded
  }, [categories, categoriesLoading]);
  const [isMobile, setIsMobile] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const heroSectionRef = useRef(null);
  const autoTurnTimerRef = useRef(null);
  const imageCycleTimerRef = useRef(null);
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);
  const touchEndX = useRef(null);
  const touchEndY = useRef(null);

  const [imagesLoading, setImagesLoading] = useState(true);

  // Categories are now fetched from shared context

  // Load all images from local folders - NO FIREBASE CALLS
  const fetchAllImages = useCallback(async () => {
    try {
      // ==================== LOAD ALL LOCAL IMAGES ====================
      
      // Hero images (desktop) - From Firebase Storage bucket
      const hardcodedHeroImage = {
        id: 'hero-desktop',
        alt: 'Hero Image - Vastré By Vijh',
        sizes: {
          '480w': 'https://images.vastrebyvijh.com/vastre-images/home-hero/desktop/2025-08-26/home-hero_desktop_1756239228357_0_480w.webp',
          '768w': 'https://images.vastrebyvijh.com/vastre-images/home-hero/desktop/2025-08-26/home-hero_desktop_1756239228357_0_768w.webp',
          '1080w': 'https://images.vastrebyvijh.com/vastre-images/home-hero/desktop/2025-08-26/home-hero_desktop_1756239228357_0_1080w.webp',
          '1440w': 'https://images.vastrebyvijh.com/vastre-images/home-hero/desktop/2025-08-26/home-hero_desktop_1756239228357_0_1440w.webp',
          '1920w': 'https://images.vastrebyvijh.com/vastre-images/home-hero/desktop/2025-08-26/home-hero_desktop_1756239228357_0_1920w.webp',
          'fallback': '/images/home-hero/home-hero_desktop_1756134915647_0.webp'
        }
      };
      
      setHeroImages([hardcodedHeroImage]);
      setHeroImagesLoading(false);
      
      // About images - From Firebase Storage bucket
      const hardcodedAboutImage = {
        id: 'about-desktop',
        alt: 'Bespoke Suit - Vastré By Vijh',
        sizes: {
          '480w': 'https://images.vastrebyvijh.com/vastre-images/home-about/desktop/2025-08-26/home-about_desktop_1756239593157_0_480w.webp',
          '768w': 'https://images.vastrebyvijh.com/vastre-images/home-about/desktop/2025-08-26/home-about_desktop_1756239593157_0_768w.webp',
          '1080w': 'https://images.vastrebyvijh.com/vastre-images/home-about/desktop/2025-08-26/home-about_desktop_1756239593157_0_1080w.webp',
          '1440w': 'https://images.vastrebyvijh.com/vastre-images/home-about/desktop/2025-08-26/home-about_desktop_1756239593157_0_1440w.webp',
          '1920w': 'https://images.vastrebyvijh.com/vastre-images/home-about/desktop/2025-08-26/home-about_desktop_1756239593157_0_1920w.webp',
          'fallback': '/images/home-about/home-about_desktop_1756135609955_0.webp'
        }
      };
      
      setAboutImages([hardcodedAboutImage]);
      setAboutImagesLoading(false);
      
      // Logo image - From Firebase Storage bucket with multiple responsive sizes
      const hardcodedLogoImage = {
        id: 'logo-desktop',
            alt: 'Vastré By Vijh Logo',
        sizes: {
          '480w': 'https://images.vastrebyvijh.com/vastre-images/logo/desktop/2025-08-26/logo_desktop_1756240073154_0_480w.webp',
          '768w': 'https://images.vastrebyvijh.com/vastre-images/logo/desktop/2025-08-26/logo_desktop_1756240073154_0_768w.webp',
          '1080w': 'https://images.vastrebyvijh.com/vastre-images/logo/desktop/2025-08-26/logo_desktop_1756240073154_0_1080w.webp',
          '1440w': 'https://images.vastrebyvijh.com/vastre-images/logo/desktop/2025-08-26/logo_desktop_1756240073154_0_1440w.webp',
          '1920w': 'https://images.vastrebyvijh.com/vastre-images/logo/desktop/2025-08-26/logo_desktop_1756240073154_0_1920w.webp',
          'fallback': '/images/logo/hero_desktop_1756135482771_0.webp'
        }
      };
      setLogoImage(hardcodedLogoImage.sizes['1080w']); // Set the main logo for now
      setLogoLoading(false);
          
      // Mobile Hero Images - From Firebase Storage bucket
      const hardcodedMobileHeroImages = [
        {
          id: 'mobile-hero-1',
          alt: 'Mobile Hero Image 1 - Vastré By Vijh',
          sizes: {
            '480w': 'https://images.vastrebyvijh.com/vastre-images/home-hero/mobile/2025-08-26/home-hero_mobile_1756241574388_0_480w.webp',
            '768w': 'https://images.vastrebyvijh.com/vastre-images/home-hero/mobile/2025-08-26/home-hero_mobile_1756241574388_0_768w.webp',
            '1080w': 'https://images.vastrebyvijh.com/vastre-images/home-hero/mobile/2025-08-26/home-hero_mobile_1756241574388_0_1080w.webp',
            '1440w': 'https://images.vastrebyvijh.com/vastre-images/home-hero/mobile/2025-08-26/home-hero_mobile_1756241574388_0_1440w.webp',
            '1920w': 'https://images.vastrebyvijh.com/vastre-images/home-hero/mobile/2025-08-26/home-hero_mobile_1756241574388_0_1920w.webp',
            'fallback': '/images/hero-home-mobile-1/1home-hero_mobile_1756136123246_0.webp'
          }
        },
        {
          id: 'mobile-hero-2',
          alt: 'Mobile Hero Image 2 - Vastré By Vijh',
          sizes: {
            '480w': 'https://images.vastrebyvijh.com/vastre-images/home-hero/mobile/2025-08-26/home-hero_mobile_1756241590830_1_480w.webp',
            '768w': 'https://images.vastrebyvijh.com/vastre-images/home-hero/mobile/2025-08-26/home-hero_mobile_1756241590830_1_768w.webp',
            '1080w': 'https://images.vastrebyvijh.com/vastre-images/home-hero/mobile/2025-08-26/home-hero_mobile_1756241590830_1_1080w.webp',
            '1440w': 'https://images.vastrebyvijh.com/vastre-images/home-hero/mobile/2025-08-26/home-hero_mobile_1756241590830_1_1440w.webp',
            '1920w': 'https://images.vastrebyvijh.com/vastre-images/home-hero/mobile/2025-08-26/home-hero_mobile_1756241590830_1_1920w.webp',
            'fallback': '/images/hero-home-mobile-2/2home-hero_mobile_1756136137869_1.webp'
          }
        },
        {
          id: 'mobile-hero-3',
          alt: 'Mobile Hero Image 3 - Vastré By Vijh',
          sizes: {
            '480w': 'https://images.vastrebyvijh.com/vastre-images/home-hero/mobile/2025-08-26/home-hero_mobile_1756241604756_2_480w.webp',
            '768w': 'https://images.vastrebyvijh.com/vastre-images/home-hero/mobile/2025-08-26/home-hero_mobile_1756241604756_2_768w.webp',
            '1080w': 'https://images.vastrebyvijh.com/vastre-images/home-hero/mobile/2025-08-26/home-hero_mobile_1756241604756_2_1080w.webp',
            '1440w': 'https://images.vastrebyvijh.com/vastre-images/home-hero/mobile/2025-08-26/home-hero_mobile_1756241604756_2_1440w.webp',
            '1920w': 'https://images.vastrebyvijh.com/vastre-images/home-hero/mobile/2025-08-26/home-hero_mobile_1756241604756_2_1920w.webp',
            'fallback': '/images/hero-home-mobile-3/home-hero_mobile_1756134915647_0.webp'
          }
        }
      ];
      
      // Store mobile hero images in a way that can be accessed by the mobile sections
      window.mobileHeroImages = hardcodedMobileHeroImages;
      
    } catch (error) {
      console.error('Error setting hardcoded images:', error);
      // Use fallback images if local loading fails
      setHeroImages([{ 
        id: 'fallback-hero',
        alt: 'Hero Image - Vastré By Vijh',
        sizes: { 'fallback': '/images/fallbacks/hero-1.webp' }
      }]);
      setAboutImages([{ 
        id: 'fallback-about',
        alt: 'Bespoke Suit - Vastré By Vijh',
        sizes: { 'fallback': '/images/fallbacks/suit.webp' }
      }]);
              setLogoImage(null);
    } finally {
      setHeroImagesLoading(false);
      setAboutImagesLoading(false);
      setLogoLoading(false);
    }
  }, []);

  // Fetch images when component mounts or device type changes
  useEffect(() => {
    fetchAllImages();
  }, [fetchAllImages]);

  // Check if device is mobile with debounced resize handler
  useEffect(() => {
    const checkMobile = () => {
      const newIsMobile = window.innerWidth <= 768;
      if (newIsMobile !== isMobile) {
        setIsMobile(newIsMobile);
      }
    };

    // Debounced resize handler to prevent excessive calls
    const debouncedCheckMobile = debounce(checkMobile, 200);

    checkMobile(); // Initial check
    window.addEventListener('resize', debouncedCheckMobile);

    return () => {
      window.removeEventListener('resize', debouncedCheckMobile);
    };
  }, [isMobile]);

  // Handle window resize for responsive images
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Auto-turn functionality for mobile
  useEffect(() => {
    if (isMobile) {
      // Auto-turn after 3 seconds for better user experience
      autoTurnTimerRef.current = setTimeout(() => {
        setIsTurned(true);
      }, 3000);

      return () => {
        if (autoTurnTimerRef.current) {
          clearTimeout(autoTurnTimerRef.current);
          autoTurnTimerRef.current = null;
        }
      };
    }
  }, [isMobile]);

  // Image cycling functionality
  useEffect(() => {
    if (isMobile && isTurned) {
      // Start cycling through images every 4 seconds
      imageCycleTimerRef.current = setInterval(() => {
        setCurrentImageIndex((prevIndex) => {
          const mobileHeroLength = window.mobileHeroImages ? window.mobileHeroImages.length : 1;
          const newIndex = (prevIndex + 1) % mobileHeroLength;
          return newIndex;
        });
      }, 4000);

      return () => {
        if (imageCycleTimerRef.current) {
          clearInterval(imageCycleTimerRef.current);
          imageCycleTimerRef.current = null;
        }
      };
    }
  }, [isMobile, isTurned]);

  // Handle manual turn
  const handleTurn = () => {
    setIsTurned(!isTurned);
    
    // Reset auto-turn timer
    if (autoTurnTimerRef.current) {
      clearTimeout(autoTurnTimerRef.current);
      autoTurnTimerRef.current = null;
    }
    
    if (!isTurned) {
      // If turning to back, set auto-return after 12 seconds (3 images × 4 seconds)
      autoTurnTimerRef.current = setTimeout(() => {
        setIsTurned(false);
        setCurrentImageIndex(0); // Reset to first image
      }, 12000);
    } else {
      // If returning to front, reset image index
      setCurrentImageIndex(0);
    }
  };

  // Handle image dot navigation
  const handleDotClick = (index) => {
    setCurrentImageIndex(index);
    
    // Reset the auto-return timer when manually navigating
    if (autoTurnTimerRef.current) {
      clearTimeout(autoTurnTimerRef.current);
      autoTurnTimerRef.current = null;
    }
    autoTurnTimerRef.current = setTimeout(() => {
      setIsTurned(false);
      setCurrentImageIndex(0);
    }, 12000);
  };

  // Enhanced touch handling for smoother swipe gestures
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.touches[0].clientX;
    touchEndY.current = e.touches[0].clientY;
  };

  const handleTouchEnd = () => {
    if (!touchStartX.current || !touchEndX.current || !touchStartY.current || !touchEndY.current) return;

    const deltaX = touchStartX.current - touchEndX.current;
    const deltaY = touchStartY.current - touchEndY.current;
    
    // Check if swipe is more horizontal than vertical (prevent accidental swipes)
    if (Math.abs(deltaX) < Math.abs(deltaY) * 2) return;
    
    const isLeftSwipe = deltaX > 50;
    const isRightSwipe = deltaX < -50;

    if (isLeftSwipe && !isTurned) {
      // Swipe left to turn to model images
      setIsTurned(true);
    } else if (isRightSwipe && isTurned) {
      // Swipe right to return to front
      setIsTurned(false);
      setCurrentImageIndex(0);
    } else if (isLeftSwipe && isTurned) {
      // Swipe left to next image
      setCurrentImageIndex((prevIndex) => {
        const mobileHeroLength = window.mobileHeroImages ? window.mobileHeroImages.length : 1;
        return (prevIndex + 1) % mobileHeroLength;
      });
    } else if (isRightSwipe && isTurned) {
      // Swipe right to previous image
      setCurrentImageIndex((prevIndex) => {
        const mobileHeroLength = window.mobileHeroImages ? window.mobileHeroImages.length : 1;
        return (prevIndex - 1 + mobileHeroLength) % mobileHeroLength;
      });
    }

    // Reset touch values
    touchStartX.current = null;
    touchEndX.current = null;
    touchStartY.current = null;
    touchEndY.current = null;
  };

  // Handle scroll-triggered turn
  useEffect(() => {
    if (!isMobile) return;

    const handleScroll = () => {
      if (heroSectionRef.current) {
        const rect = heroSectionRef.current.getBoundingClientRect();
        const isVisible = rect.top < window.innerHeight && rect.bottom > 0;
        
        if (isVisible && !isTurned) {
          // Trigger turn when hero section comes into view
          setTimeout(() => {
            setIsTurned(true);
          }, 2000);
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isMobile, isTurned]);

  // Reset timers when component unmounts
  useEffect(() => {
    return () => {
      if (autoTurnTimerRef.current) {
        clearTimeout(autoTurnTimerRef.current);
      }
      if (imageCycleTimerRef.current) {
        clearInterval(imageCycleTimerRef.current);
      }
    };
  }, []);

  return (
    <>
      <SEOHead {...homePageSEO} />
      {/* Main Video Loader */}
      <Loader isLoading={isVideoLoading} />
      
      {/* Video Loading Progress */}
      {isVideoLoading && videoLoadingProgress > 0 && (
        <div className="video-loading-progress">
          <div className="progress-bar">
            <div 
              className="progress-fill" 
              style={{ width: `${videoLoadingProgress}%` }}
            ></div>
          </div>
          <p className="progress-text">
            Loading video... {Math.round(videoLoadingProgress)}%
          </p>
        </div>
      )}
      
      
      <div className="home">
      {/* Aesthetic Loader */}
      {heroImagesLoading && (
        <div className="aesthetic-loader">
          <div className="loader-content">
            <div className="fashion-loader">
              <div className="stitch-line"></div>
              <div className="stitch-line"></div>
              <div className="stitch-line"></div>
            </div>
            <h2>Vastré By Vijh</h2>
            <p>Crafting Excellence</p>
          </div>
        </div>
      )}
      
      {/* Hero Section - Full Screen Video */}
      <section className="hero-section" style={{ display: showVideoContent ? 'block' : 'none' }}>
                {/* Full Screen Video Background */}
        <div className="hero-video-container">
          <video
            ref={videoRef}
            className="hero-video"
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            // poster="https://images.vastrebyvijh.com/vastre-images/home-hero/desktop/2025-08-26/home-hero_desktop_1756239228357_0_1920w.webp"
            onLoadedMetadata={(e) => {
              if (!e.target) return;
              // Set playback rate to 0.7 (slower speed) for cinematic effect
              e.target.playbackRate = 0.7;
            }}
            onLoadedData={(e) => {
              if (!e.target) return;
              // Hide fallback layout when video loads successfully
              const heroSection = e.target.closest('.hero-section');
              if (heroSection) {
                heroSection.classList.remove('fallback-mode');
                heroSection.classList.add('video-loaded');
              }
            }}
            onCanPlay={(e) => {
              if (!e.target) return;
              // Video is ready to play
              e.target.play().catch(err => console.log('Auto-play prevented:', err));
            }}
            onError={(e) => {
              if (!e.target) return;
              console.error('Hero video failed to load:', e.target.src);
              // Fallback to original split design if video fails
              e.target.style.display = 'none';
              const fallbackImg = e.target.nextElementSibling;
              if (fallbackImg) fallbackImg.style.display = 'block';
              // Show original split layout
              const heroSection = e.target.closest('.hero-section');
              if (heroSection) {
                heroSection.classList.add('fallback-mode');
                const fallbackLayout = heroSection.querySelector('.hero-fallback-layout');
                if (fallbackLayout) {
                  fallbackLayout.style.display = 'flex';
                }
              }
            }}
          >
            {/* Responsive WebM video sources based on device size */}
            <source src={videoCDNUrls.desktop} type="video/webm" media="(min-width: 1025px)" />
            <source src={videoCDNUrls.tablet} type="video/webm" media="(min-width: 481px) and (max-width: 1024px)" />
            <source src={videoCDNUrls.mobile} type="video/webm" media="(max-width: 480px)" />
            {/* Fallback MP4 for older browsers */}
            <source src="https://images.vastrebyvijh.com/vastre-images/hero-video/hero-video.mp4" type="video/mp4" />
          </video>

          {/* Fallback image */}
          <img
            src="https://images.vastrebyvijh.com/vastre-images/home-hero/desktop/2025-08-26/home-hero_desktop_1756239228357_0_1920w.webp"
            alt="Hero Image - Vastré By Vijh"
            className="hero-fallback-image"
            style={{ display: 'none' }}
            loading="eager"
            fetchpriority="high"
          />
            </div>

                {/* Text Overlay - Hidden when video is playing */}
        <div className="hero-content" style={{ display: 'none' }}>
        </div>

                {/* Scroll Indicator */}
        <div className="scroll-indicator" onClick={() => {
          const nextSection = document.querySelector('.collections-section, .featured-products, .about-preview, .testimonials');
          if (nextSection) {
            nextSection.scrollIntoView({ behavior: 'smooth' });
          }
        }}>
          <div className="scroll-arrow">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M7 14L12 19L17 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M7 5L12 10L17 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <span className="scroll-text">Scroll to explore</span>
        </div>

        {/* Original Split Layout - Only visible in fallback mode */}
        <div className="hero-fallback-layout" style={{ display: 'none' }}>
        <div className="hero-left">
          <div className="hero-logo-area">
            {logoLoading ? (
              <div className="logo-loading">
                <div className="logo-skeleton"></div>
              </div>
            ) : logoImage ? (
              <img
                src={logoImage}
                alt="Vastré By Vijh Logo"
                className="hero-logo"
                loading="eager"
                fetchpriority="high"
                onError={(e) => {
                  // Firebase logo failed to load, using default
                }}
              />
            ) : (
                              <div className="no-logo-placeholder">
                  <p>Logo</p>
                </div>
            )}
            <div className="hero-tagline">Crafted With Rarity</div>
          </div>

          <Link to="/collections" className="view-collection-btn">
            VIEW COLLECTION
          </Link>
        </div>

        <div className="hero-right">
            <img
              src="https://images.vastrebyvijh.com/vastre-images/home-hero/desktop/2025-08-26/home-hero_desktop_1756239228357_0_1920w.webp"
              alt="Hero Image - Vastré By Vijh"
              className="hero-image"
              loading="eager"
              fetchpriority="high"
            />
            </div>
        </div>
      </section>

      {/* Elegant Couture Section */}
      <section className="elegant-couture">
        <div className="couture-content">
          <div className="couture-text">
            <h2>Elegant Couture</h2>
            <p>Where traditional tailoring meets modern design, creating timeless pieces that define sophistication and elegance.</p>
            <Link to="/collections" className="couture-cta">
              EXPLORE THE CRAFT
            </Link>
          </div>
        </div>
      </section>

      {/* About Us Section */}
      <AboutSection aboutImages={aboutImages} aboutImagesLoading={aboutImagesLoading} />

      {/* Collections Section */}
      <section className="collections-section">
        <div className="collections-content">
          <div className="collections-header">
            <h2>Our Collections</h2>
            <Link to="/collections" className="view-all-link">
              View All Collections →
            </Link>
          </div>
          <div className="collections-grid">
            {categoriesLoading ? (
              // Loading skeleton
              Array.from({ length: 4 }).map((_, index) => (
                <div key={index} className="collection-card loading">
                  <div className="collection-image skeleton">
                    <div className="skeleton-image"></div>
                    <div className="collection-overlay">
                      <div className="skeleton-text"></div>
                      <div className="skeleton-text short"></div>
                    </div>
                  </div>
                </div>
              ))
            ) : homePageCollections.length > 0 ? (
              // Render specific 4 collections from database: Shirts, Blazer, Ethnic Sets, and Pants
              homePageCollections.map((category) => {
                // Handle responsive image URLs from database structure
                const getImageSources = () => {
                  if (category.imageURLs?.optimized) {
                    const sources = [];
                    const sizeOrder = ['480w', '768w', '1080w', '1440w', '1920w'];
                    
                    sizeOrder.forEach(size => {
                      if (category.imageURLs.optimized[size]?.url) {
                        sources.push({
                          srcset: category.imageURLs.optimized[size].url,
                          media: size === '480w' ? '(max-width: 480px)' :
                                 size === '768w' ? '(max-width: 768px)' :
                                 size === '1080w' ? '(max-width: 1080px)' :
                                 size === '1440w' ? '(max-width: 1440px)' :
                                 '(min-width: 1441px)'
                        });
                      }
                    });
                    
                    return sources;
                  }
                  return [];
                };
                
                const sources = getImageSources();
                const fallbackUrl = category.imageURLs?.original?.url || '/images/fallbacks/formal-wear.webp';
                
                return (
                  <Link 
                    key={category.id} 
                    to={`/collections?category=${category.id}`} 
                    className="collection-card-link"
                  >
                    <div className="collection-card">
                      <div className="collection-image">
                        {sources.length > 0 ? (
                          <picture>
                            {sources.map((source, index) => (
                              <source
                                key={index}
                                srcSet={source.srcset}
                                media={source.media}
                                type="image/webp"
                                sizes="(max-width: 600px) 100vw, (max-width: 1200px) 50vw, 25vw"
                              />
                            ))}
                            <img
                              src={fallbackUrl}
                              alt={`${category.categoryName || category.id} Collection`}
                              className="collection-model-image"
                              loading="lazy"
                              sizes="(max-width: 600px) 100vw, (max-width: 1200px) 50vw, 25vw"
                              onError={(e) => {
                                console.error('Category image failed to load:', e.target.src);
                                e.target.src = '/images/fallbacks/formal-wear.webp';
                              }}
                            />
                          </picture>
                        ) : (
                          <img
                            src={fallbackUrl}
                            alt={`${category.categoryName || category.id} Collection`}
                            className="collection-model-image"
                            loading="lazy"
                            sizes="(max-width: 600px) 100vw, (max-width: 1200px) 50vw, 25vw"
                            onError={(e) => {
                              console.error('Category image failed to load:', e.target.src);
                              e.target.src = '/images/fallbacks/formal-wear.webp';
                            }}
                          />
                        )}
                        <div className="collection-overlay">
                          <h3>{category.categoryName || category.id}</h3>
                          <span className="collection-cta">
                            SHOP NOW
                          </span>
                        </div>
                      </div>
                    </div>
                  </Link>
                );
              })
            ) : (
              // Beautiful "Coming Soon" section when no categories
              <div className="coming-soon-section">
                <div className="coming-soon-content">
                  <div className="coming-soon-icon">
                    <svg width="80" height="80" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 2L13.09 8.26L20 9L13.09 9.74L12 16L10.91 9.74L4 9L10.91 8.26L12 2Z" fill="#D6A464"/>
                      <path d="M19 15L19.74 17.74L22.5 18.5L19.74 19.26L19 22L18.26 19.26L15.5 18.5L18.26 17.74L19 15Z" fill="#D6A464"/>
                      <path d="M5 6L5.37 7.37L6.74 7.74L5.37 8.11L5 9.48L4.63 8.11L3.26 7.74L4.63 7.37L5 6Z" fill="#D6A464"/>
                    </svg>
                </div>
                  <h2 className="coming-soon-title">Something Amazing is Coming</h2>
                  <p className="coming-soon-description">
                    We're crafting something extraordinary for our collections. 
                    Our artisans are working tirelessly to bring you the finest quality pieces.
                  </p>
                  <div className="coming-soon-features">
                    <div className="feature-item">
                      <div className="feature-icon">✨</div>
                      <span>Premium Quality</span>
                    </div>
                    <div className="feature-item">
                      <div className="feature-icon">🎨</div>
                      <span>Handcrafted</span>
                    </div>
                    <div className="feature-item">
                      <div className="feature-icon">💎</div>
                      <span>Exclusive Design</span>
                    </div>
                  </div>
                  <div className="coming-soon-actions">
                    <button className="notify-me-btn">
                      <span>Notify Me When Ready</span>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M5 12H19M12 5L19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                    <Link to="/collections" className="browse-other-btn">
                      Browse Collections
                    </Link>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Luxury Cashback Floating Icon */}
      <LuxuryCashbackDisplay 
        orderTotal={0} 
        position="bottom-right" 
        showFloatingIcon={true}
        autoOpen={true}
        pageKey="home"
      />

      </div>
    </>
  );
};

export default Home; 